import React, { useEffect, useMemo } from "react";
import { Card, CardContent, Paper, Stack, Typography, Box } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import LoadingOrError from "../../../global-layout/LoadingOrError";
import NoProjectTasksFound from "../pages/NoProjectTasksFound";
import ProjectDetailsTaskCard from "./ProjectDetailsTaskCard";
import ProjectHorizontalScrollButtons from "./ProjectHorizontalScrollButtons";
import { FilterSortGroup } from "../projectTypes";
import {
  IncludeExcludeFilters,
  PicklistItem,
  UseGetProjectDetailHook,
} from "../canonical";
import {
  getPicklistValue,
  getPicklistValueByLabel,
} from "../../../functions/picklistValue";
import {
  formatDateOnly,
  getLabelsForCoreStatus,
  mapToCoreStatus,
} from "../utils/ProjectDetailsUtilities";

const normalizeStatusFilters = (filters: any, picklist: unknown) => {
  if (!filters) return filters;

  const { assignee, status, ...rest } = filters;
  if (!filters?.status) {
    return { ...rest, assignedResourceID: assignee };
  }
  const statusIds = filters.status.flatMap((coreStatus: string) => {
    const rawLabels = getLabelsForCoreStatus(coreStatus);
    return rawLabels
      .map((label) => {
        const picklistValue = getPicklistValueByLabel(
          picklist,
          "project",
          "status",
          label,
        );
        return picklistValue?.value == 2 ? 8 : picklistValue?.value;
      })
      .filter((v) => v !== undefined);
  });

  return {
    status: statusIds,
    assignedResourceID: assignee,
    ...rest,
  };
};

const COMPLETED_STATUS_ID = 5;

interface ProjectDetailsGroupColumnsProps {
  projectId: number | undefined;
  filterSortGroup: FilterSortGroup;
  picklist?: PicklistItem[] | undefined;
  setPhases: (value: string[]) => void;
  hideCompletedTasks: boolean;
  useGetProjectDetail: UseGetProjectDetailHook;
}

const ProjectDetailsGroupColumns = ({
  projectId,
  filterSortGroup,
  picklist,
  setPhases,
  hideCompletedTasks,
  useGetProjectDetail,
}: ProjectDetailsGroupColumnsProps) => {
  const initialIncludeFilters = filterSortGroup.filters.currentFilters?.include;
  const initialExcludeFilters = filterSortGroup.filters.currentFilters?.exclude;

  const includeFilters = useMemo(
    () => normalizeStatusFilters(initialIncludeFilters, picklist),
    [initialIncludeFilters, picklist],
  );
  const excludeFilters = useMemo(
    () => normalizeStatusFilters(initialExcludeFilters, picklist),
    [initialExcludeFilters, picklist],
  );

  const request = {
    projectId: projectId,
    includeFilters: includeFilters
      ? (includeFilters as IncludeExcludeFilters)
      : undefined,
    excludeFilters: excludeFilters
      ? (excludeFilters as IncludeExcludeFilters)
      : undefined,
    groupBy: filterSortGroup.grouping?.id ?? undefined,
    sortBy: filterSortGroup.sort?.id ?? undefined,
  };

  const { data, isLoading, isFetching, isError } = useGetProjectDetail(request);

  useEffect(() => {
    if (!data?.projectTasksGroups) return;

    const newPhases: string[] = [];
    const seen = new Set<string>();

    for (const group of data.projectTasksGroups) {
      for (const task of group.projectTasks ?? []) {
        const phase = task.phase;
        if (phase && !seen.has(phase)) {
          seen.add(phase);
          newPhases.push(phase);
        }
      }
    }

    if (newPhases.length > 0) {
      setPhases(newPhases);
    }
  }, [data, setPhases]);

  const filteredGroups = useMemo(() => {
    return data?.projectTasksGroups
      ?.map((group) => {
        const filteredTasks = hideCompletedTasks
          ? group?.projectTasks?.filter(
              (task) => task.status !== COMPLETED_STATUS_ID,
            )
          : group?.projectTasks;

        return {
          ...group,
          filteredTasks,
          visibleTaskCount: filteredTasks?.length ?? 0,
        };
      })
      .filter((group) => group.visibleTaskCount > 0);
  }, [data?.projectTasksGroups, hideCompletedTasks]);

  const totalTasks =
    data?.projectTasksGroups?.reduce(
      (acc, group) => acc + (group.totalTasks ?? 0),
      0,
    ) ?? 0;

  if (isLoading || isError)
    return (
      <LoadingOrError
        error={isError}
        message={"Service engagement tasks cannot be loaded"}
        loading={isLoading || isFetching}
      />
    );

  if (totalTasks === 0)
    return (
      <Grid item xs={12}>
        <Paper>
          <NoProjectTasksFound />
        </Paper>
      </Grid>
    );

  return (
    <ProjectHorizontalScrollButtons>
      <Stack direction="row" spacing={2}>
        {filteredGroups?.map((group, index) => {
          return (
            <Card
              key={`${group.title ?? "untitled"}-${index}`}
              sx={{
                minWidth: 450,
                backgroundColor: "background.default",
              }}
            >
              <CardContent>
                <Typography>
                  {filterSortGroup.grouping?.id !== "status"
                    ? group.title
                    : mapToCoreStatus(
                        getPicklistValue(
                          picklist,
                          "project",
                          "status",
                          group.title === "8" ? "2" : group.title,
                        ).label,
                      )}
                </Typography>
                <Box display="flex" justifyContent="space-between" pb={1}>
                  {filterSortGroup.grouping?.id !== "status" ? (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {hideCompletedTasks
                          ? `${(group.totalTasks ?? 0) - (group.completedTasks ?? 0)} of ${group.totalTasks} remaining`
                          : `${group.completedTasks} of ${group.totalTasks} completed`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateOnly(group.projectStartDate)}
                        {" - "}
                        {formatDateOnly(group.projectEndDate)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {group.visibleTaskCount}{" "}
                      {group.visibleTaskCount === 1 ? "task" : "tasks"}
                    </Typography>
                  )}
                </Box>
                <Stack
                  spacing={1}
                  sx={{
                    overflow: "scroll",
                    scrollbarWidth: "none",
                    height: 700,
                  }}
                >
                  {group.filteredTasks?.map((task) => (
                    <ProjectDetailsTaskCard
                      key={task.id}
                      task={task}
                      filterSortGroup={filterSortGroup}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </ProjectHorizontalScrollButtons>
  );
};

export default ProjectDetailsGroupColumns;
